import { useState } from "react";
import { API, apiErr } from "@/api";
import { toast } from "sonner";
import { Heart, ChevronUp, ChevronDown, X, Lock, Send } from "lucide-react";

export default function InterestTray({ interest, exhibitorName, className = "sticky bottom-0" }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const { items, shared, unshared_count: fresh } = interest.view;
  if (!items.length) return null;

  const doShare = async () => {
    setBusy(true);
    try { await interest.share(); toast.success(`Shared with ${exhibitorName || "the exhibitor"}`); setConfirm(false); setConsent(false); }
    catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className={`${className} z-20 mt-4`} data-testid="interest-tray">
      <div className="rounded-2xl bg-white shadow-[0_-6px_24px_rgba(0,0,0,0.12)] border border-amber-500/30 p-3">
        <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2" data-testid="interest-tray-toggle">
          <Heart size={16} className="text-rose-500" fill="currentColor" />
          <span className="text-sm font-medium text-slate-800" data-testid="interest-count">{items.length} selected</span>
          <span className="flex -space-x-2 ml-1">
            {items.slice(0, 4).map((i) => <img key={i.product_id} src={`${API}/files/${i.image}`} alt="" className="h-7 w-7 rounded-full object-cover border-2 border-white" />)}
          </span>
          <span className="ml-auto text-[11px] text-slate-500">{shared ? (fresh ? `${fresh} not shared yet` : "Shared") : "Private"}</span>
          {open ? <ChevronDown size={16} className="text-slate-500" /> : <ChevronUp size={16} className="text-slate-500" />}
        </button>
        {open && (
          <div className="mt-3">
            <div className="max-h-44 overflow-y-auto space-y-1.5">
              {items.map((i) => (
                <div key={i.product_id} className="flex items-center gap-2" data-testid={`interest-item-${i.product_id}`}>
                  <img src={`${API}/files/${i.image}`} alt="" className="h-9 w-9 rounded-lg object-cover" />
                  <span className="flex-1 text-xs text-slate-700 truncate">{i.name}</span>
                  <button onClick={() => interest.toggle(i.product_id)} className="text-slate-400" data-testid={`interest-remove-${i.product_id}`}><X size={14} /></button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-500 mt-2 flex items-start gap-1"><Lock size={11} className="mt-0.5 shrink-0" /> {shared ? (fresh ? "Shared earlier. New selections stay private until you share updates." : `Shared with ${exhibitorName || "the exhibitor"}.`) : "Your selections are private until you choose to share them."}</p>
            {!confirm ? (
              (!shared || fresh > 0) && <button onClick={() => setConfirm(true)} className="w-full btn-gold py-2.5 rounded-xl mt-2 text-sm flex items-center justify-center gap-1.5" data-testid="interest-share-button"><Send size={14} /> {shared ? "Share updates with Exhibitor" : "Share Interests with Exhibitor"}</button>
            ) : (
              <div className="mt-2 rounded-xl bg-amber-50 border border-amber-300 p-3" data-testid="interest-share-confirm">
                <p className="text-[11px] text-slate-700">{exhibitorName || "The exhibitor"} will receive your <b>name, phone number and email</b> along with these {items.length} product(s), and may contact you about them.</p>
                <label className="flex items-start gap-2 text-[11px] text-slate-700 mt-2">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-[#C6A15B]" data-testid="interest-consent" /> I agree to share my details and selections.
                </label>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => setConfirm(false)} className="flex-1 py-2 rounded-lg text-xs text-slate-600 border border-slate-300" data-testid="interest-share-cancel">Cancel</button>
                  <button disabled={!consent || busy} onClick={doShare} className="flex-1 btn-gold py-2 rounded-lg text-xs disabled:opacity-50" data-testid="interest-share-submit">{busy ? "Sharing…" : "Share"}</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
